import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { SignalProgress } from "@/components/ui/signal-progress";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, MapPin, Target, User, Briefcase,
  Link as LinkIcon, BookOpen, MessageCircle, Loader2, Plus, Flame
} from "lucide-react";
import { useTalentMarketplace } from "@/hooks/useTalentMarketplace";
import { useAdminOrgContext } from "@/hooks/useAdminOrgContext";
import { useShortlists } from "@/hooks/useShortlists";
import { useCareerSignals } from "@/hooks/useCareerSignals";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSkillById } from "@/lib/skills";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildWhatsAppLink } from "@/lib/whatsapp";

export default function AdminTalentoDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orgId } = useAdminOrgContext();
  const { getProfilePublic, logAudit } = useTalentMarketplace(orgId);
  const { shortlists, addItem, createShortlist } = useShortlists(orgId);

  const [profileData, setProfileData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    logAudit.mutate({ action: "view_profile", entityType: "profile", entityId: id });
    getProfilePublic(id).then((data) => {
      setProfileData(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const { data: studentSkills } = useQuery({
    queryKey: ["student_skills_detail", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_skills" as any)
        .select("skill_id")
        .eq("user_id", id!);
      if (error) throw error;
      return (data as any[]).map((s: any) => s.skill_id as string);
    },
  });

  const { data: phoneData } = useQuery({
    queryKey: ["talent_phone_admin", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("phone")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return (data as any)?.phone as string | null;
    },
  });

  // Career signals for this talent
  const { data: talentSignals } = useQuery({
    queryKey: ["talent_career_signals", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile_career_signals")
        .select("signal_id, status")
        .eq("user_id", id!);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { signals: allSignals } = useCareerSignals();
  const completedSignals = (talentSignals ?? []).filter(s => s.status === "done").length;
  const totalSignals = allSignals.length || 6;

  const handleAddToShortlist = (shortlistId: string, shortlistName: string) => {
    if (!id) return;
    addItem.mutate({ shortlistId, userId: id }, {
      onSuccess: () => toast.success(`Adicionado à shortlist "${shortlistName}"`),
      onError: () => toast.error("Erro ao adicionar à shortlist"),
    });
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const profile = profileData?.profile;
  const experiences = profileData?.experiences ?? [];
  const portfolio = profileData?.portfolio ?? [];
  const courses = profileData?.courses ?? [];
  const interests = profileData?.interests ?? [];
  const warmups = profileData?.warmups ?? [];

  if (!profile) {
    return (
      <div className="p-8 text-center py-20">
        <p className="text-muted-foreground">Perfil não encontrado ou não está visível.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate("/admin/vitrine")}>
          <ArrowLeft className="w-4 h-4 mr-2" />Voltar
        </Button>
      </div>
    );
  }

  const whatsappLink = buildWhatsAppLink(phoneData ?? null, profile.full_name ?? "");

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/vitrine")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <span className="text-sm text-muted-foreground">Perfil do Talento</span>
      </motion.div>

      {/* Main Info Card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-6 shadow-card border border-border mb-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
            <User className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl font-bold text-foreground">{profile.full_name ?? "Sem nome"}</h1>
              <StatusBadge status={(profile.profile_status ?? "construction") as any} />
              <SignalProgress current={completedSignals} total={totalSignals} />
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {(profile.city || profile.state) && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />{[profile.city, profile.state].filter(Boolean).join(", ")}
                </span>
              )}
              {profile.objective && (
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />{profile.objective}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Bio */}
        {profile.bio && (
          <div className="bg-secondary rounded-xl p-4 mb-4">
            <p className="text-sm text-foreground">{profile.bio}</p>
          </div>
        )}

        {/* Interests */}
        {interests.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {interests.map((i: any, idx: number) => (
              <Badge key={idx} variant="secondary">{i.interest_tags?.name ?? i.tag_id}</Badge>
            ))}
          </div>
        )}

        {/* Skills */}
        {(studentSkills ?? []).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {(studentSkills ?? []).map((sid) => (
              <Badge key={sid} variant="outline" className="text-xs">{getSkillById(sid)?.label ?? sid}</Badge>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex-1">
                <Plus className="w-4 h-4" />Adicionar à Shortlist
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {shortlists.length === 0 ? (
                <DropdownMenuItem disabled>Nenhuma shortlist criada</DropdownMenuItem>
              ) : (
                shortlists.map((sl: any) => (
                  <DropdownMenuItem key={sl.id} onClick={() => handleAddToShortlist(sl.id, sl.name)}>
                    {sl.name}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {whatsappLink ? (
            <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => window.open(whatsappLink, "_blank")}>
              <MessageCircle className="w-4 h-4" />WhatsApp
            </Button>
          ) : (
            <Button variant="outline" className="flex-1" disabled>
              <MessageCircle className="w-4 h-4" />Sem telefone
            </Button>
          )}
        </div>
      </motion.div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Experiências */}
        {experiences.length > 0 && (
          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-base font-semibold text-foreground">Experiências</h2>
            </div>
            <div className="space-y-3">
              {experiences.map((exp: any) => (
                <div key={exp.id} className="bg-card rounded-xl p-4 border border-border">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-foreground">{exp.title}</h3>
                    <Badge variant="outline" className="text-xs">{exp.exp_type}</Badge>
                  </div>
                  {exp.description && <p className="text-sm text-muted-foreground mb-2">{exp.description}</p>}
                  {exp.learning_outcome && (
                    <div className="bg-accent/10 rounded-lg p-3 border border-accent/20">
                      <p className="text-xs font-medium text-foreground">O que aprendi:</p>
                      <p className="text-xs text-muted-foreground">{exp.learning_outcome}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.section>
        )}

        <div className="space-y-6">
          {/* Portfólio */}
          {portfolio.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <div className="flex items-center gap-2 mb-4">
                <LinkIcon className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-base font-semibold text-foreground">Portfólio</h2>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="space-y-2">
                  {portfolio.map((link: any) => (
                    <div key={link.id} className="flex items-center gap-2 text-sm">
                      <Badge variant="secondary" className="text-xs">{link.platform}</Badge>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">{link.label || link.url}</a>
                    </div>
                  ))}
                </div>
              </div>
            </motion.section>
          )}

          {/* Cursos Realizados */}
          {courses.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-base font-semibold text-foreground">Cursos Realizados</h2>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="space-y-2">
                  {courses.map((curso: any) => (
                    <div key={curso.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{curso.course_name}</span>
                      <span className="text-muted-foreground text-xs">
                        {[curso.provider, curso.year_label].filter(Boolean).join(" • ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.section>
          )}

          {/* Aquecimentos */}
          {warmups.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-base font-semibold text-foreground">Aquecimentos</h2>
              </div>
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="space-y-2">
                  {warmups.map((w: any) => (
                    <div key={w.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{(w as any).warmup_prompts?.title ?? "Aquecimento"}</span>
                      <span className="text-muted-foreground text-xs">
                        {w.submitted_at ? format(new Date(w.submitted_at), "dd/MM/yyyy") : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.section>
          )}
        </div>
      </div>

      {/* Footer */}
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="text-center text-xs text-muted-foreground mt-8">
        Este perfil representa uma trajetória, não um currículo perfeito.
      </motion.p>
    </div>
  );
}
